import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Column, DropdownMenuItem, DropdownSubMenu, TableList, TableListActions, Typography } from 'seagull';
import { DoorEnter, Edit, Trash, UserAdd } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { AssociatedClientLink } from './AssociatedClientLink';
import { ClientPortalMenuItem } from './ClientPortalMenuItem';
import styles from './ContactsDashboardRow.module.scss';
import { associatedCustomersOf, canDeleteContact, contactFullName, isUnassociatedContact } from './ContactsDashboardRow.utils';
export const GetContactsDashboardRowTID = (parentTestId)=>getTestIds(parentTestId);
export const ContactsDashboardRow = (param)=>{
    let { contact, onEdit, onAddToAnotherClient, onDelete, onViewClientPortal, testId } = param;
    const { ContactsDashboardRowTestIds } = useMemo(()=>GetContactsDashboardRowTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('pages.contactsDashboard');
    const isUnassociated = isUnassociatedContact(contact);
    const associatedCustomers = associatedCustomersOf(contact);
    const fullName = contactFullName(contact);
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: contact.contactId,
        testId: testId,
        items: [
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: ContactsDashboardRowTestIds.Name(),
                    children: fullName
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    testId: ContactsDashboardRowTestIds.Email(),
                    children: contact.email
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    testId: ContactsDashboardRowTestIds.Phone(),
                    children: contact.phone
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Column, {
                    className: styles.associatedClients,
                    testId: ContactsDashboardRowTestIds.AssociatedClients(),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        children: associatedCustomers.map((customer, index)=>/*#__PURE__*/ _jsx(AssociatedClientLink, {
                                customer: customer,
                                showSeparator: index > 0,
                                testId: ContactsDashboardRowTestIds.ClientLink(customer.customerId)
                            }, customer.customerId))
                    })
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            bi: {
                button: {
                    name: 'contact_view'
                },
                menuTrigger: {
                    name: 'contact_actions'
                }
            },
            testId: ContactsDashboardRowTestIds.Actions(),
            onClickButton: ()=>onEdit(contact),
            mobile: {
                title: fullName
            },
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_edit'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    testId: ContactsDashboardRowTestIds.Edit(),
                    onClick: ()=>onEdit(contact),
                    children: t.actions.edit()
                }, "edit"),
                !isUnassociated && /*#__PURE__*/ _jsx(DropdownSubMenu, {
                    bi: {
                        name: 'contact_view_client_portal'
                    },
                    iconStart: /*#__PURE__*/ _jsx(DoorEnter, {}),
                    label: t.actions.viewClientPortal(),
                    testId: ContactsDashboardRowTestIds.ViewClientPortal(),
                    children: associatedCustomers.map((customer)=>/*#__PURE__*/ _jsx(ClientPortalMenuItem, {
                            customer: customer,
                            testId: ContactsDashboardRowTestIds.PortalClient(customer.customerId),
                            onClick: (customerId)=>onViewClientPortal(contact, customerId)
                        }, customer.customerId))
                }, "view-client-portal"),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_add_to_another_client'
                    },
                    iconStart: /*#__PURE__*/ _jsx(UserAdd, {}),
                    testId: ContactsDashboardRowTestIds.AddToAnotherClient(),
                    onClick: ()=>onAddToAnotherClient(contact),
                    children: t.actions.addToAnotherClient()
                }, "add-to-another-client"),
                canDeleteContact(contact) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_delete'
                    },
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    testId: ContactsDashboardRowTestIds.Delete(),
                    onClick: ()=>onDelete(contact),
                    children: t.actions.delete()
                }, "delete")
            ]
        })
    });
};
